// ================================================================
// BOOKING CALENDAR COMPONENT
// Flow: 1. Choose Staff → 2. Select Meeting Type → 3. Pick Date & Time → 4. Book
// ================================================================

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import Layout from '../components/Layout';
import '../components/booking-calendar.css';
import '../components/booking-components.css';

const MEETING_ICONS = {
  'technical_support': '\u{1F527}', 'technical-support': '\u{1F527}',
  'sales': '\u{1F4BC}', 'sales_consultation': '\u{1F4BC}', 'sales-consultation': '\u{1F4BC}',
  'quotation_discussion': '\u{1F4B0}', 'quotation-discussion': '\u{1F4B0}',
  'consultation': '\u{1F4CA}', 'demo': '\u{1F5A5}', 'training': '\u{1F4DA}',
  'onboarding': '\u{1F680}', 'customer_inquiries': '\u{1F4AC}', 'customer-inquiries': '\u{1F4AC}'
};

// 👥 Multi-participant states
  
  
// ✅ Helper: format date as YYYY-MM-DD in LOCAL timezone (not UTC)
const toLocalDateStr = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

// ────────────────────────────────────────────────
// Timeline helpers
// ────────────────────────────────────────────────
const tMins = (t) => { const [h, m] = t.split(':').map(Number); return h * 60 + m; };
const fMins = (m) => `${String(Math.floor(m / 60)).padStart(2,'0')}:${String(m % 60).padStart(2,'0')}`;
const snap5 = (m) => Math.round(m / 5) * 5;

// ────────────────────────────────────────────────
// BookingTimeline component
// ────────────────────────────────────────────────
const BookingTimeline = ({ dayStart, dayEnd, dayBookings, availableSlots, onTimeSelect, defaultDuration, onBookingMove, onBookingClick }) => {
  const GRID_H = 600;
  const TOTAL  = dayEnd - dayStart;
  const gridRef = React.useRef(null);
  const interact = React.useRef(null);
  const [sel, setSel] = React.useState(null);
  // drag state for existing bookings
  const bookingDrag = React.useRef(null);
  const [draggingId, setDraggingId] = React.useState(null);

  const pct  = (m) => ((m - dayStart) / TOTAL) * 100;
  const getM = (y) => {
    const r = gridRef.current.getBoundingClientRect();
    return snap5(dayStart + (Math.max(0, Math.min(y - r.top, r.height)) / r.height) * TOTAL);
  };

  const isBooked = (fM, tM, ignoreId = null) =>
    dayBookings.some(b => b.id !== ignoreId && fM < tMins(b.end_time) && tM > tMins(b.start_time));

  // ── Grid mouse: draw new selection ──
  const onMouseDown = (e) => {
    if (e.button !== 0 || e.target.closest('[data-block]')) return;
    e.preventDefault();
    const m = getM(e.clientY);
    if (isBooked(m, m + 5)) return;
    interact.current = { startM: m };
    setSel({ from: m, to: Math.min(m + defaultDuration, dayEnd) });
  };

  // ── Booking block mouse: start drag ──
  const onBookingMouseDown = (e, booking) => {
    e.stopPropagation();
    e.preventDefault();
    const clickM = getM(e.clientY);
    const fromM  = tMins(booking.start_time);
    const dur    = tMins(booking.end_time) - fromM;
    bookingDrag.current = { id: booking.id, offset: clickM - fromM, dur, originalFrom: fromM };
    setDraggingId(booking.id);
    interact.current = null;
    setSel(null);
  };

  const onMouseMove = React.useCallback((e) => {
    if (!gridRef.current) return;

    // Drag existing booking
    if (bookingDrag.current) {
      const m = getM(e.clientY);
      const { id, offset, dur } = bookingDrag.current;
      const newFrom = Math.max(dayStart, Math.min(dayEnd - dur, m - offset));
      const newTo   = newFrom + dur;
      if (!isBooked(newFrom, newTo, id)) {
        // Live preview: update via callback (optimistic)
        bookingDrag.current.previewFrom = newFrom;
        bookingDrag.current.previewTo   = newTo;
        setDraggingId(id + '_' + newFrom); // force re-render
      }
      return;
    }

    // Draw new selection
    if (!interact.current) return;
    const m = getM(e.clientY);
    const from = Math.min(interact.current.startM, m);
    const to   = Math.max(interact.current.startM + 5, m);
    if (!isBooked(from, to)) setSel({ from: Math.max(dayStart, from), to: Math.min(dayEnd, to) });
  }, [dayBookings, dayStart, dayEnd]);

  const onMouseUp = React.useCallback((e) => {
    // Finish booking drag
    if (bookingDrag.current) {
      const { id, previewFrom, previewTo, dur, originalFrom } = bookingDrag.current;
      bookingDrag.current = null;
      setDraggingId(null);
      // Only save if moved
      if (previewFrom !== undefined && previewFrom !== originalFrom) {
        onBookingMove(id, fMins(previewFrom), fMins(previewTo ?? previewFrom + dur));
      }
      return;
    }
    // Finish draw
    if (interact.current && sel) onTimeSelect(sel.from, sel.to);
    interact.current = null;
  }, [sel, onBookingMove]);

  React.useEffect(() => {
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    return () => { window.removeEventListener('mousemove', onMouseMove); window.removeEventListener('mouseup', onMouseUp); };
  }, [onMouseMove, onMouseUp]);

  const hours = [];
  for (let h = Math.ceil(dayStart / 60); h <= Math.floor(dayEnd / 60); h++) hours.push(h);

  // Compute live booking positions (with drag preview)
  const getBookingPos = (b) => {
    if (bookingDrag.current && bookingDrag.current.id === b.id && bookingDrag.current.previewFrom !== undefined) {
      return { from: bookingDrag.current.previewFrom, to: bookingDrag.current.previewTo };
    }
    return { from: tMins(b.start_time), to: tMins(b.end_time) };
  };

  return (
    <div style={{ display: 'flex', height: GRID_H, userSelect: 'none' }}>
      {/* Hour labels */}
      <div style={{ width: 44, flexShrink: 0, position: 'relative' }}>
        {hours.map(h => (
          <div key={h} style={{
            position: 'absolute', top: `${pct(h * 60)}%`,
            right: 6, fontSize: 11, color: 'var(--text-tertiary)',
            transform: 'translateY(-50%)', fontWeight: 500
          }}>
            {h < 12 ? `${h} AM` : h === 12 ? '12 PM' : `${h-12} PM`}
          </div>
        ))}
      </div>

      {/* Grid */}
      <div ref={gridRef} onMouseDown={onMouseDown}
        style={{ flex: 1, position: 'relative',
          cursor: bookingDrag.current ? 'grabbing' : 'crosshair',
          borderLeft: '1px solid var(--border-primary)', background: 'var(--bg-card)' }}>

        {/* Lines */}
        {hours.map((h, i) => (
          <React.Fragment key={h}>
            <div style={{ position: 'absolute', left: 0, right: 0, top: `${pct(h * 60)}%`,
              borderTop: `1px solid ${i === 0 ? 'transparent' : 'var(--border-primary)'}` }} />
            <div style={{ position: 'absolute', left: 0, right: 0, top: `${pct(h * 60 + 30)}%`,
              borderTop: '1px dashed var(--border-secondary)', opacity: 0.5 }} />
          </React.Fragment>
        ))}

        {/* Available tint */}
        {availableSlots.map((s, i) => {
          const fM = tMins(s.start_time), tM = tMins(s.end_time);
          return <div key={i} style={{
            position: 'absolute', left: 0, right: 0,
            top: `${pct(fM)}%`, height: `${((tM - fM) / TOTAL) * 100}%`,
            background: 'rgba(99,102,241,0.04)', pointerEvents: 'none'
          }} />;
        })}

        {/* Existing bookings */}
        {dayBookings.map((b) => {
          const { from: fM, to: tM } = getBookingPos(b);
          const dur = tM - fM;
          const isDragging = bookingDrag.current?.id === b.id;
          return (
            <div key={b.id} data-block="booking"
              onMouseDown={(e) => onBookingMouseDown(e, b)}
              onClick={(e) => { if (!isDragging) { e.stopPropagation(); onBookingClick(b); } }}
              title="Click to edit · Drag to move"
              style={{
                position: 'absolute', left: 4, right: 4,
                top: `${pct(fM)}%`, height: `${((tM - fM) / TOTAL) * 100}%`,
                background: isDragging ? 'rgba(99,102,241,0.3)' : 'rgba(99,102,241,0.15)',
                border: `1px solid var(--color-primary)`,
                borderLeft: '3px solid var(--color-primary)',
                borderRadius: 6, padding: '3px 8px', overflow: 'hidden',
                cursor: 'grab', zIndex: isDragging ? 10 : 2,
                opacity: isDragging ? 0.8 : 1,
                boxShadow: isDragging ? '0 4px 16px rgba(99,102,241,0.4)' : 'none',
                transition: isDragging ? 'none' : 'box-shadow 0.2s'
              }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--color-primary-light)',
                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                ✏️ {fMins(fM)}–{fMins(tM)} · {b.customer_name}
              </div>
              {dur >= 30 && (
                <div style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>{b.meeting_type || ''} · {dur} min</div>
              )}
              {dur >= 45 && (
                <div style={{ fontSize: 10, color: 'var(--text-disabled)', marginTop: 2 }}>Click to edit</div>
              )}
            </div>
          );
        })}

        {/* New selection preview */}
        {sel && (
          <div style={{
            position: 'absolute', left: 4, right: 4,
            top: `${pct(sel.from)}%`, height: `${((sel.to - sel.from) / TOTAL) * 100}%`,
            background: 'rgba(16,185,129,0.15)',
            border: '2px solid #10b981', borderLeft: '4px solid #10b981',
            borderRadius: 6, padding: '3px 8px', zIndex: 3, pointerEvents: 'none'
          }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#10b981' }}>
              {fMins(sel.from)} – {fMins(sel.to)}
            </div>
            <div style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>{sel.to - sel.from} min</div>
          </div>
        )}
      </div>
    </div>
  );
};
// ────────────────────────────────────────────────
// BookingPanel modal component
// ────────────────────────────────────────────────
const BookingPanel = ({ initialFrom, initialTo, date, staff, eventType, dayBookings, dayEnd,
  onSubmit, onClose, submitting, submitResult }) => {
  const [fromH, setFromH] = React.useState(Math.floor(initialFrom / 60));
  const [fromM, setFromM] = React.useState(initialFrom % 60);
  const [toH,   setToH  ] = React.useState(Math.floor(initialTo / 60));
  const [toM,   setToM  ] = React.useState(initialTo % 60);

  const [form, setForm] = React.useState({
    customer_name: '', customer_phone: '', customer_email: '',
    language: 'ar', meeting_reason: '', connection_method: ''
  });

  // 👥 Participants
  const [selectedParticipants, setSelectedParticipants] = React.useState([]);
  const [availableParticipants, setAvailableParticipants] = React.useState([]);
  const [loadingParticipants, setLoadingParticipants] = React.useState(false);
  const [showParticipantSelector, setShowParticipantSelector] = React.useState(false);

  const fromMins_ = fromH * 60 + fromM;
  const toMins_   = toH   * 60 + toM;
  const duration  = toMins_ - fromMins_;

  const fetchParticipants = async () => {
    setLoadingParticipants(true);
    try {
      const response = await api.get('/bookings/available-participants', {
        params: {
          booking_date: date,
          start_time: fMins(fromMins_),
          end_time: fMins(toMins_),
          exclude_agent_id: staff.id
        }
      });
      const data = response.data;
      if (data && data.success) setAvailableParticipants(data.data || []);
    } catch (err) { console.error('Error fetching participants:', err); }
    setLoadingParticipants(false);
  };

  const toggleParticipant = (agent) => {
    setSelectedParticipants(prev =>
      prev.find(p => p.id === agent.id) ? prev.filter(p => p.id !== agent.id) : [...prev, agent]
    );
  };

  const removeParticipant = (id) => setSelectedParticipants(prev => prev.filter(p => p.id !== id));

  // Compute max end: next booking start after fromMins_ or dayEnd
  const maxEnd = React.useMemo(() => {
    const nexts = dayBookings.map(b => tMins(b.start_time)).filter(m => m > fromMins_).sort((a,b)=>a-b);
    return nexts[0] ?? dayEnd;
  }, [dayBookings, fromMins_, dayEnd]);

  // Spinners
  const SpinH = ({ val, setVal, min, max }) => (
    <div style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:2 }}>
      <button type="button" onClick={() => setVal(v => Math.min(max, v+1))} style={panelSpinBtn}>▲</button>
      <span style={{ fontSize:22, fontWeight:800, color:'var(--color-primary-light)', minWidth:28, textAlign:'center' }}>{String(val).padStart(2,'0')}</span>
      <button type="button" onClick={() => setVal(v => Math.max(min, v-1))} style={panelSpinBtn}>▼</button>
    </div>
  );
  const SpinM = ({ val, setVal, isTo }) => (
    <div style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:2 }}>
      <button type="button" onClick={() => {
        const next = (val + 5) % 60;
        const newTotal = (isTo ? toH : fromH) * 60 + next;
        if (isTo && newTotal <= maxEnd && newTotal > fromMins_) setToM(next);
        if (!isTo) setFromM(next);
      }} style={panelSpinBtn}>▲</button>
      <span style={{ fontSize:22, fontWeight:800, color:'var(--color-primary-light)', minWidth:28, textAlign:'center' }}>{String(val).padStart(2,'0')}</span>
      <button type="button" onClick={() => {
        const prev = ((val - 5) + 60) % 60;
        const newTotal = (isTo ? toH : fromH) * 60 + prev;
        if (isTo && newTotal > fromMins_) setToM(prev);
        if (!isTo) setFromM(prev);
      }} style={panelSpinBtn}>▼</button>
    </div>
  );

  const methods = (() => {
    try {
      return typeof eventType?.connection_methods === 'string'
        ? JSON.parse(eventType.connection_methods)
        : (eventType?.connection_methods || []);
    } catch { return []; }
  })();

  const valid = duration >= 5 && form.customer_name.trim() && form.customer_phone.trim();

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!valid) return;
    onSubmit({
      slot: { date, start_time: fMins(fromMins_), end_time: fMins(toMins_), display: fMins(fromMins_) },
      form,
      participant_ids: selectedParticipants.map(p => p.id)
    });
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1000, backdropFilter: 'blur(4px)'
    }}>
      <div style={{
        background: 'var(--bg-card)', borderRadius: 20, width: 500,
        maxHeight: '90vh', overflowY: 'auto',
        boxShadow: '0 24px 60px rgba(0,0,0,0.4)',
        border: '1px solid var(--border-accent)'
      }}>
        {/* Header */}
        <div style={{
          background: 'var(--color-primary-gradient)', padding: '18px 24px',
          borderRadius: '20px 20px 0 0',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center'
        }}>
          <div style={{ color: '#fff' }}>
            <div style={{ fontSize: 16, fontWeight: 700 }}>📅 New Booking</div>
            <div style={{ fontSize: 12, opacity: 0.85, marginTop: 2 }}>
              {new Date(date + 'T12:00:00').toLocaleDateString('en-US', { weekday:'long', month:'long', day:'numeric', year:'numeric' })}
            </div>
          </div>
          <button onClick={onClose} style={{
            background: 'rgba(255,255,255,0.2)', border: 'none',
            borderRadius: 8, width: 32, height: 32,
            color: '#fff', cursor: 'pointer', fontSize: 18
          }}>✕</button>
        </div>

        <div style={{ padding: '22px 24px' }}>
          {submitResult && submitResult.success ? (
            <div style={{ textAlign: 'center', padding: '24px 0' }}>
              <div style={{ fontSize: 56 }}>✅</div>
              <h3 style={{ color: 'var(--text-primary)', marginTop: 12 }}>Booking Confirmed!</h3>
              <div style={{ background: 'var(--bg-tertiary)', borderRadius: 12, padding: '14px 20px', margin: '16px 0', fontSize: 14, color: 'var(--text-secondary)', lineHeight: 1.8 }}>
                🕐 {fMins(fromMins_)} – {fMins(toMins_)} &nbsp;·&nbsp; {duration} min<br/>
                👤 {form.customer_name} &nbsp;·&nbsp; 📱 {form.customer_phone}
              </div>
              <button onClick={onClose} className="btn btn-primary" style={{ marginTop: 8 }}>Done</button>
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              {submitResult && !submitResult.success && (
                <div style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.4)', borderRadius: 10, padding: '10px 14px', marginBottom: 16, color: '#f87171', fontSize: 13 }}>
                  ❌ {submitResult.message}
                </div>
              )}

              {/* Time pickers */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', gap: 10, alignItems: 'center', marginBottom: 18 }}>
                <div style={{ background: 'var(--bg-tertiary)', borderRadius: 12, padding: 14, border: '2px solid var(--color-primary)', textAlign: 'center' }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--color-primary-light)', letterSpacing: 1, marginBottom: 10 }}>🟢 FROM</div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                    <SpinH val={fromH} setVal={setFromH} min={0} max={23} />
                    <span style={{ fontSize: 22, fontWeight: 800, color: 'var(--text-primary)' }}>:</span>
                    <SpinM val={fromM} setVal={setFromM} isTo={false} />
                  </div>
                  <div style={{ marginTop: 8, fontSize: 20, fontWeight: 800, color: 'var(--color-primary-light)' }}>{fMins(fromMins_)}</div>
                </div>

                <div style={{ color: 'var(--text-tertiary)', fontSize: 20, textAlign: 'center' }}>→</div>

                <div style={{ background: 'var(--bg-tertiary)', borderRadius: 12, padding: 14, border: '2px solid var(--border-accent)', textAlign: 'center' }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-tertiary)', letterSpacing: 1, marginBottom: 10 }}>🔴 TO</div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                    <SpinH val={toH} setVal={v => { if (v * 60 + toM > fromMins_ && v * 60 + toM <= maxEnd) setToH(v); }} min={fromH} max={23} />
                    <span style={{ fontSize: 22, fontWeight: 800, color: 'var(--text-primary)' }}>:</span>
                    <SpinM val={toM} setVal={setToM} isTo={true} />
                  </div>
                  <div style={{ marginTop: 8, fontSize: 20, fontWeight: 800, color: 'var(--text-primary)' }}>{fMins(toMins_)}</div>
                  <div style={{ fontSize: 11, color: 'rgba(239,68,68,0.8)', marginTop: 4 }}>Max: {fMins(maxEnd)}</div>
                </div>
              </div>

              {/* Duration + quick buttons */}
              <div style={{ marginBottom: 16 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                  {duration > 0 && (
                    <span style={{ background: 'rgba(99,102,241,0.15)', color: 'var(--color-primary-light)', borderRadius: 20, padding: '3px 12px', fontSize: 12, fontWeight: 700 }}>
                      {duration} min
                    </span>
                  )}
                  <span style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>Quick:</span>
                </div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {[15, 30, 45, 60, 90].map(d => {
                    const endM = fromMins_ + d;
                    const ok = endM <= maxEnd;
                    return (
                      <button key={d} type="button" disabled={!ok}
                        onClick={() => { setToH(Math.floor(endM / 60)); setToM(endM % 60); }}
                        style={{
                          background: duration === d ? 'var(--color-primary)' : ok ? 'var(--bg-tertiary)' : 'var(--bg-secondary)',
                          color: duration === d ? '#fff' : ok ? 'var(--text-primary)' : 'var(--text-disabled)',
                          border: `1px solid ${duration === d ? 'var(--color-primary)' : 'var(--border-primary)'}`,
                          borderRadius: 20, padding: '5px 12px', fontSize: 12, fontWeight: 600,
                          cursor: ok ? 'pointer' : 'not-allowed'
                        }}>{d}m</button>
                    );
                  })}
                </div>
              </div>

              <hr style={{ border: 'none', borderTop: '1px solid var(--border-primary)', margin: '0 0 16px' }} />

              {/* Form fields */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                <div className="form-group">
                  <label className="form-label">Customer Name *</label>
                  <input className="form-input" type="text" placeholder="Enter name"
                    value={form.customer_name} onChange={e => setForm(p => ({...p, customer_name: e.target.value}))} required />
                </div>
                <div className="form-group">
                  <label className="form-label">WhatsApp *</label>
                  <input className="form-input" type="tel" placeholder="+20 1xx xxxx xxxx"
                    value={form.customer_phone} onChange={e => setForm(p => ({...p, customer_phone: e.target.value}))} required />
                </div>
                <div className="form-group">
                  <label className="form-label">Email (optional)</label>
                  <input className="form-input" type="email" placeholder="email@example.com"
                    value={form.customer_email} onChange={e => setForm(p => ({...p, customer_email: e.target.value}))} />
                </div>
                <div className="form-group">
                  <label className="form-label">Language</label>
                  <select className="form-select" value={form.language} onChange={e => setForm(p => ({...p, language: e.target.value}))}>
                    <option value="ar">العربية</option>
                    <option value="en">English</option>
                  </select>
                </div>
                {methods.length > 0 && (
                  <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                    <label className="form-label">Meeting Method *</label>
                    <select className="form-select" value={form.connection_method} onChange={e => setForm(p => ({...p, connection_method: e.target.value}))} required>
                      <option value="">-- Select --</option>
                      {methods.map((m, i) => (
                        <option key={i} value={m}>
                          {m === 'google_meet' ? '📹 Google Meet' : m === 'zoom' ? '💻 Zoom' : m === 'phone' ? '📞 Phone' : m === 'in_person' ? '🏢 In Person' : m}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>

              <div className="form-group" style={{ marginBottom: 20 }}>
                <label className="form-label">Notes</label>
                <textarea className="form-textarea" rows={2}
                  placeholder="Meeting purpose or notes..."
                  value={form.meeting_reason} onChange={e => setForm(p => ({...p, meeting_reason: e.target.value}))} />
              </div>

              {/* 👥 Participants */}
              <div style={{
                marginBottom: 20, padding: 16,
                background: 'rgba(139,92,246,0.08)',
                borderRadius: 12, border: '1px solid rgba(139,92,246,0.25)'
              }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center',
                  marginBottom: (selectedParticipants.length > 0 || showParticipantSelector) ? 12 : 0 }}>
                  <h4 style={{ margin:0, color:'#a78bfa', fontSize:14 }}>👥 Additional Participants (Optional)</h4>
                  <button type="button"
                    onClick={() => {
                      setShowParticipantSelector(v => !v);
                      if (!showParticipantSelector) fetchParticipants();
                    }}
                    style={{
                      background:'none', border:`1px solid ${showParticipantSelector ? '#f87171' : '#a78bfa'}`,
                      color: showParticipantSelector ? '#f87171' : '#a78bfa',
                      borderRadius:8, padding:'4px 12px', fontSize:12, cursor:'pointer', fontWeight:600
                    }}>
                    {showParticipantSelector ? '✕ Close' : '+ Add Team Member'}
                  </button>
                </div>

                {/* Selected tags */}
                {selectedParticipants.length > 0 && (
                  <div style={{ display:'flex', flexWrap:'wrap', gap:6, marginBottom: showParticipantSelector ? 12 : 0 }}>
                    {selectedParticipants.map(p => (
                      <span key={p.id} style={{
                        display:'inline-flex', alignItems:'center', gap:5,
                        padding:'4px 10px', background:'rgba(34,197,94,0.15)',
                        border:'1px solid rgba(34,197,94,0.4)', borderRadius:20,
                        fontSize:12, color:'#4ade80'
                      }}>
                        <span style={{ width:18, height:18, borderRadius:'50%', background:'#8b5cf6',
                          display:'inline-flex', alignItems:'center', justifyContent:'center',
                          fontSize:10, color:'#fff' }}>{p.full_name?.charAt(0)?.toUpperCase()}</span>
                        {p.full_name}
                        <button type="button" onClick={() => removeParticipant(p.id)}
                          style={{ background:'none', border:'none', color:'#f87171', cursor:'pointer', fontSize:14, padding:0, lineHeight:1 }}>×</button>
                      </span>
                    ))}
                  </div>
                )}

                {/* Selector grid */}
                {showParticipantSelector && (
                  <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(180px,1fr))', gap:6 }}>
                    {loadingParticipants ? (
                      <div style={{ gridColumn:'1/-1', textAlign:'center', padding:16, color:'var(--text-tertiary)', fontSize:13 }}>Loading team members...</div>
                    ) : availableParticipants.length === 0 ? (
                      <div style={{ gridColumn:'1/-1', textAlign:'center', padding:16, color:'var(--text-tertiary)', fontSize:13 }}>No other team members available</div>
                    ) : availableParticipants.map(agent => {
                      const isSelected = !!selectedParticipants.find(p => p.id === agent.id);
                      const isBusy = !agent.is_available;
                      return (
                        <div key={agent.id} onClick={() => !isBusy && toggleParticipant(agent)} style={{
                          display:'flex', alignItems:'center', gap:8, padding:'8px 10px',
                          borderRadius:10, cursor: isBusy ? 'not-allowed' : 'pointer', opacity: isBusy ? 0.4 : 1,
                          background: isSelected ? 'rgba(139,92,246,0.25)' : 'rgba(255,255,255,0.04)',
                          border:`2px solid ${isSelected ? '#8b5cf6' : isBusy ? '#ef4444' : 'rgba(255,255,255,0.08)'}`,
                          transition:'all 0.15s'
                        }}>
                          <div style={{ width:30, height:30, borderRadius:'50%',
                            background: isSelected ? '#8b5cf6' : '#374151',
                            display:'flex', alignItems:'center', justifyContent:'center',
                            fontSize:12, color:'#fff', flexShrink:0 }}>
                            {isSelected ? '✓' : agent.full_name?.charAt(0)?.toUpperCase()}
                          </div>
                          <div style={{ overflow:'hidden' }}>
                            <div style={{ fontSize:12, fontWeight:600, color: isBusy ? '#9ca3af' : '#fff',
                              whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{agent.full_name}</div>
                            {isBusy && <div style={{ fontSize:11, color:'#ef4444' }}>⚠ Busy</div>}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              <div style={{ display: 'flex', gap: 10 }}>
                <button type="button" className="btn btn-outline" onClick={onClose}>Cancel</button>
                <button type="submit" className="btn btn-primary" style={{ flex: 1 }} disabled={!valid || submitting}>
                  {submitting ? '⏳ Booking...' : valid ? `✅ Book · ${fMins(fromMins_)} → ${fMins(toMins_)}` : 'Fill required fields'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

// ────────────────────────────────────────────────
// EditBookingPanel — edit / cancel existing booking
// ────────────────────────────────────────────────
const EditBookingPanel = ({ booking, onClose, onSaved, onCancelled }) => {
  const [bookingDate, setBookingDate] = React.useState(
    typeof booking.booking_date === 'string'
      ? booking.booking_date.split('T')[0]
      : booking.booking_date
  );
  const [fromH, setFromH] = React.useState(Math.floor(tMins(booking.start_time) / 60));
  const [fromM, setFromM] = React.useState(tMins(booking.start_time) % 60);
  const [toH,   setToH  ] = React.useState(Math.floor(tMins(booking.end_time) / 60));
  const [toM,   setToM  ] = React.useState(tMins(booking.end_time) % 60);
  const [updateReason, setUpdateReason] = React.useState('');
  const [cancelReason, setCancelReason] = React.useState('');
  const [tab, setTab] = React.useState('edit'); // 'edit' | 'cancel'
  const [saving, setSaving] = React.useState(false);
  const [result, setResult] = React.useState(null);

  const fromMins_ = fromH * 60 + fromM;
  const toMins_   = toH   * 60 + toM;
  const duration  = toMins_ - fromMins_;

  const SpinH = ({ val, setVal, min = 0, max = 23 }) => (
    <div style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:2 }}>
      <button type="button" onClick={() => setVal(v => Math.min(max, v+1))} style={panelSpinBtn}>▲</button>
      <span style={{ fontSize:22, fontWeight:800, color:'var(--color-primary-light)', minWidth:28, textAlign:'center' }}>{String(val).padStart(2,'0')}</span>
      <button type="button" onClick={() => setVal(v => Math.max(min, v-1))} style={panelSpinBtn}>▼</button>
    </div>
  );
  const SpinM = ({ val, setVal, isTo }) => (
    <div style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:2 }}>
      <button type="button" onClick={() => { const n=(val+5)%60; if(!isTo||toH*60+n>fromMins_) setVal(n); }} style={panelSpinBtn}>▲</button>
      <span style={{ fontSize:22, fontWeight:800, color:'var(--color-primary-light)', minWidth:28, textAlign:'center' }}>{String(val).padStart(2,'0')}</span>
      <button type="button" onClick={() => { const n=((val-5)+60)%60; if(!isTo||toH*60+n>fromMins_) setVal(n); }} style={panelSpinBtn}>▼</button>
    </div>
  );

  const handleSave = async () => {
    setSaving(true); setResult(null);
    try {
      await api.put(`/bookings/${booking.id}/update`, {
        booking_date: bookingDate,
        start_time: fMins(fromMins_),
        end_time: fMins(toMins_),
        update_reason: updateReason || 'Time updated by agent'
      });
      setResult({ success: true, message: 'Booking updated!' });
      setTimeout(() => { onSaved({ ...booking, booking_date: bookingDate, start_time: fMins(fromMins_), end_time: fMins(toMins_) }); }, 1200);
    } catch (err) {
      setResult({ success: false, message: err.response?.data?.message || 'Failed to update' });
    } finally { setSaving(false); }
  };

  const handleCancel = async () => {
    if (!cancelReason.trim()) { setResult({ success: false, message: 'Please enter a cancellation reason' }); return; }
    setSaving(true); setResult(null);
    try {
      await api.put(`/bookings/${booking.id}/cancel`, { reason: cancelReason });
      setResult({ success: true, message: 'Booking cancelled.' });
      setTimeout(() => onCancelled(booking.id), 1200);
    } catch (err) {
      setResult({ success: false, message: err.response?.data?.message || 'Failed to cancel' });
    } finally { setSaving(false); }
  };

  const statusColor = { pending:'#f59e0b', confirmed:'#10b981', cancelled:'#ef4444', completed:'#6366f1' };

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.6)',
      display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000, backdropFilter:'blur(4px)' }}>
      <div style={{ background:'var(--bg-card)', borderRadius:20, width:480,
        maxHeight:'90vh', overflowY:'auto',
        boxShadow:'0 24px 60px rgba(0,0,0,0.4)', border:'1px solid var(--border-accent)' }}>

        {/* Header */}
        <div style={{ background:'var(--color-primary-gradient)', padding:'18px 24px',
          borderRadius:'20px 20px 0 0', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <div style={{ color:'#fff' }}>
            <div style={{ fontSize:15, fontWeight:700 }}>✏️ Edit Booking</div>
            <div style={{ fontSize:12, opacity:0.85, marginTop:2 }}>
              {booking.customer_name} · {booking.booking_date}
            </div>
          </div>
          <div style={{ display:'flex', alignItems:'center', gap:8 }}>
            <span style={{ background: statusColor[booking.status] || '#6b7280',
              color:'#fff', borderRadius:20, padding:'3px 10px', fontSize:11, fontWeight:700 }}>
              {booking.status}
            </span>
            <button onClick={onClose} style={{ background:'rgba(255,255,255,0.2)', border:'none',
              borderRadius:8, width:30, height:30, color:'#fff', cursor:'pointer', fontSize:18 }}>✕</button>
          </div>
        </div>

        <div style={{ padding:'20px 24px' }}>

          {/* Booking info summary */}
          <div style={{ background:'var(--bg-tertiary)', borderRadius:12, padding:'12px 16px',
            marginBottom:18, fontSize:13, color:'var(--text-secondary)', display:'grid',
            gridTemplateColumns:'1fr 1fr', gap:'6px 16px' }}>
            <div>👤 <strong>{booking.customer_name}</strong></div>
            <div>📱 {booking.customer_phone}</div>
            <div>🕐 {booking.start_time} – {booking.end_time}</div>
            <div>📋 {booking.meeting_type || '—'}</div>
          </div>

          {/* Tabs */}
          <div style={{ display:'flex', gap:8, marginBottom:20 }}>
            {['edit','cancel'].map(t => (
              <button key={t} type="button" onClick={() => setTab(t)} style={{
                flex:1, padding:'9px', borderRadius:10, border:'2px solid',
                borderColor: tab===t ? 'var(--color-primary)' : 'var(--border-primary)',
                background: tab===t ? 'rgba(99,102,241,0.15)' : 'var(--bg-tertiary)',
                color: tab===t ? 'var(--color-primary-light)' : 'var(--text-secondary)',
                fontWeight:600, fontSize:13, cursor:'pointer'
              }}>{t==='edit' ? '✏️ Edit Time' : '🗑️ Cancel Booking'}</button>
            ))}
          </div>

          {result && (
            <div style={{ background: result.success ? 'rgba(16,185,129,0.1)' : 'rgba(239,68,68,0.1)',
              border:`1px solid ${result.success ? '#10b981' : '#ef4444'}`,
              borderRadius:10, padding:'10px 14px', marginBottom:14,
              color: result.success ? '#10b981' : '#f87171', fontSize:13 }}>
              {result.success ? '✅' : '❌'} {result.message}
            </div>
          )}

          {/* Edit tab */}
          {tab === 'edit' && (
            <>
              {/* Date picker */}
              <div className="form-group" style={{ marginBottom: 16 }}>
                <label className="form-label">📅 Date</label>
                <input
                  type="date"
                  className="form-input"
                  value={bookingDate}
                  min={new Date().toISOString().split('T')[0]}
                  onChange={e => setBookingDate(e.target.value)}
                />
              </div>

              <div style={{ display:'grid', gridTemplateColumns:'1fr auto 1fr', gap:10,
                alignItems:'center', marginBottom:16 }}>
                <div style={{ background:'var(--bg-tertiary)', borderRadius:12, padding:14,
                  border:'2px solid var(--color-primary)', textAlign:'center' }}>
                  <div style={{ fontSize:10, fontWeight:700, color:'var(--color-primary-light)', letterSpacing:1, marginBottom:10 }}>🟢 FROM</div>
                  <div style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:6 }}>
                    <SpinH val={fromH} setVal={setFromH} />
                    <span style={{ fontSize:20, fontWeight:800, color:'var(--text-primary)' }}>:</span>
                    <SpinM val={fromM} setVal={setFromM} isTo={false} />
                  </div>
                  <div style={{ marginTop:8, fontSize:20, fontWeight:800, color:'var(--color-primary-light)' }}>{fMins(fromMins_)}</div>
                </div>
                <div style={{ color:'var(--text-tertiary)', fontSize:20, textAlign:'center' }}>→</div>
                <div style={{ background:'var(--bg-tertiary)', borderRadius:12, padding:14,
                  border:'2px solid var(--border-accent)', textAlign:'center' }}>
                  <div style={{ fontSize:10, fontWeight:700, color:'var(--text-tertiary)', letterSpacing:1, marginBottom:10 }}>🔴 TO</div>
                  <div style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:6 }}>
                    <SpinH val={toH} setVal={v => { if(v*60+toM>fromMins_) setToH(v); }} min={fromH} />
                    <span style={{ fontSize:20, fontWeight:800, color:'var(--text-primary)' }}>:</span>
                    <SpinM val={toM} setVal={setToM} isTo={true} />
                  </div>
                  <div style={{ marginTop:8, fontSize:20, fontWeight:800, color:'var(--text-primary)' }}>{fMins(toMins_)}</div>
                </div>
              </div>

              {duration > 0 && (
                <div style={{ textAlign:'center', marginBottom:14 }}>
                  <span style={{ background:'rgba(99,102,241,0.15)', color:'var(--color-primary-light)',
                    borderRadius:20, padding:'4px 14px', fontSize:12, fontWeight:700 }}>{duration} min</span>
                </div>
              )}

              {/* Quick durations */}
              <div style={{ display:'flex', gap:6, flexWrap:'wrap', justifyContent:'center', marginBottom:16 }}>
                {[15,30,45,60,90].map(d => {
                  const endM = fromMins_ + d;
                  return (
                    <button key={d} type="button"
                      onClick={() => { setToH(Math.floor(endM/60)); setToM(endM%60); }}
                      style={{ background: duration===d ? 'var(--color-primary)' : 'var(--bg-tertiary)',
                        color: duration===d ? '#fff' : 'var(--text-primary)',
                        border:`1px solid ${duration===d ? 'var(--color-primary)' : 'var(--border-primary)'}`,
                        borderRadius:20, padding:'5px 12px', fontSize:12, fontWeight:600, cursor:'pointer' }}>
                      {d}m
                    </button>
                  );
                })}
              </div>

              <div className="form-group" style={{ marginBottom:16 }}>
                <label className="form-label">Reason for change (sent via WhatsApp)</label>
                <input className="form-input" type="text"
                  placeholder="e.g. Rescheduled by agent"
                  value={updateReason} onChange={e => setUpdateReason(e.target.value)} />
              </div>

              <div style={{ display:'flex', gap:10 }}>
                <button type="button" className="btn btn-outline" onClick={onClose}>Cancel</button>
                <button type="button" className="btn btn-primary" style={{ flex:1 }}
                  onClick={handleSave} disabled={saving || duration < 5}>
                  {saving ? '⏳ Saving...' : `✅ Save · ${fMins(fromMins_)} → ${fMins(toMins_)}`}
                </button>
              </div>
            </>
          )}

          {/* Cancel tab */}
          {tab === 'cancel' && (
            <>
              <div style={{ background:'rgba(239,68,68,0.08)', border:'1px solid rgba(239,68,68,0.3)',
                borderRadius:12, padding:'14px 16px', marginBottom:16, fontSize:13, color:'#fca5a5' }}>
                ⚠️ This will cancel the booking and notify the customer via WhatsApp.
              </div>
              <div className="form-group" style={{ marginBottom:20 }}>
                <label className="form-label">Cancellation Reason *</label>
                <textarea className="form-textarea" rows={3}
                  placeholder="e.g. Agent unavailable, customer requested cancellation..."
                  value={cancelReason} onChange={e => setCancelReason(e.target.value)} />
              </div>
              <div style={{ display:'flex', gap:10 }}>
                <button type="button" className="btn btn-outline" style={{ flex:1 }} onClick={onClose}>Keep Booking</button>
                <button type="button" onClick={handleCancel} disabled={saving || !cancelReason.trim()}
                  style={{ flex:1, background: cancelReason.trim() ? '#ef4444' : 'var(--bg-tertiary)',
                    color: cancelReason.trim() ? '#fff' : 'var(--text-disabled)',
                    border:'none', borderRadius:12, padding:'12px', fontSize:14, fontWeight:700,
                    cursor: cancelReason.trim() ? 'pointer' : 'not-allowed' }}>
                  {saving ? '⏳...' : '🗑️ Cancel Booking'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const panelSpinBtn = {
  background: 'var(--bg-tertiary)', border: '1px solid var(--border-primary)',
  borderRadius: 6, width: 28, height: 20, cursor: 'pointer',
  fontSize: 9, color: 'var(--color-primary-light)', fontWeight: 700, padding: 0
};

// ────────────────────────────────────────────────
const BookingCalendar = () => {
  const navigate = useNavigate();
  const leadDataRef = useRef(null);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [selectedEventType, setSelectedEventType] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [eventTypes, setEventTypes] = useState([]);
  const [staffLoading, setStaffLoading] = useState(false);
  const [eventTypesLoading, setEventTypesLoading] = useState(false);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [view, setView] = useState('staff');
  const [selectedSlot, setSelectedSlot] = useState(null);

  // Form fields
  const [formData, setFormData] = useState({
    customer_name: '',
    customer_phone: '',
    customer_email: '',
    language: 'ar',
    meeting_reason: '',
    connection_method: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitResult, setSubmitResult] = useState(null);
  const [dayBookings, setDayBookings] = useState([]);
  const [dayBookingsLoading, setDayBookingsLoading] = useState(false);
  const [bookingPanel, setBookingPanel] = useState(null);
  const [editBooking, setEditBooking] = useState(null); // booking to edit
 // 👥 Multi-participant states
  const [selectedParticipants, setSelectedParticipants] = useState([]);
  const [availableParticipants, setAvailableParticipants] = useState([]);
  const [loadingParticipants, setLoadingParticipants] = useState(false);
  const [showParticipantSelector, setShowParticipantSelector] = useState(false);

  useEffect(() => {
    fetchStaff();
    // Check if coming from Leads page with pre-fill data
    try {
      const raw = localStorage.getItem('booking_lead_data');
      if (raw) {
        leadDataRef.current = JSON.parse(raw);
        console.log('📋 Lead data loaded for booking pre-fill:', leadDataRef.current);
      }
    } catch (e) { console.error('Failed to parse lead data:', e); }
  }, []);

  const fetchStaff = async () => {
    setStaffLoading(true);
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const response = await api.get('/bookings/available-agents');
      const data = response.data;
      let staff = [];
      if (Array.isArray(data)) staff = data;
      else if (data && Array.isArray(data.data)) staff = data.data;
      else if (data && Array.isArray(data.agents)) staff = data.agents;

      // Check if user can manage others' bookings
      if (!user.manage_others_bookings) {
        // Only show the logged-in user
        staff = staff.filter(s => s.id === user.id);
        if (staff.length === 0) {
          // If user not in list, add them manually
          staff = [{ id: user.id, full_name: user.full_name || user.username }];
        }
      }

      setStaffList(staff);
    } catch (error) {
      console.error('Error fetching staff:', error);
      setStaffList([]);
    } finally {
      setStaffLoading(false);
    }
  };

  const fetchEventTypes = async (agentId) => {
    setEventTypesLoading(true);
    try {
      const response = await api.get('/bookings/meeting-types', {
        params: { agent_id: agentId }
      });
      let rawData = response.data !== undefined ? response.data : response;
      let types = [];
      if (rawData && rawData.success && Array.isArray(rawData.data)) types = rawData.data;
      else if (Array.isArray(rawData)) types = rawData;
      else if (rawData && Array.isArray(rawData.data)) types = rawData.data;
      setEventTypes(types);
    } catch (error) {
      console.error('Error fetching event types:', error);
      setEventTypes([]);
    } finally {
      setEventTypesLoading(false);
    }
  };

  const fetchAvailableSlots = async (staffId, eventTypeSlug) => {
    setSlotsLoading(true);
    try {
      const response = await api.get(`/bookings/slots/${staffId}`, {
        params: { meeting_type: eventTypeSlug, days_ahead: 14 }
      });
      const data = response.data;
      let slots = [];
      if (Array.isArray(data)) slots = data;
      else if (data && Array.isArray(data.data)) slots = data.data;
      else if (data && Array.isArray(data.slots)) slots = data.slots;
      setAvailableSlots(slots);
    } catch (error) {
      console.error('Error fetching slots:', error);
      setAvailableSlots([]);
    } finally {
      setSlotsLoading(false);
    }
  };

  const fetchDayBookings = async (staffId, dateStr) => {
    setDayBookingsLoading(true);
    try {
      // Fetch pending bookings
      const [r1, r2] = await Promise.all([
        api.get('/bookings', { params: { agent_id: staffId, date_from: dateStr, date_to: dateStr, status: 'pending' } }),
        api.get('/bookings', { params: { agent_id: staffId, date_from: dateStr, date_to: dateStr, status: 'confirmed' } }),
      ]);
      const list1 = r1.data?.data || r1.data || [];
      const list2 = r2.data?.data || r2.data || [];
      const combined = [...(Array.isArray(list1) ? list1 : []), ...(Array.isArray(list2) ? list2 : [])];
      setDayBookings(combined);
    } catch (err) {
      console.error('Error fetching day bookings:', err);
      setDayBookings([]);
    } finally {
      setDayBookingsLoading(false);
    }
  };

  const handleBookingMove = async (bookingId, newStart, newEnd) => {
    try {
      const b = dayBookings.find(x => x.id === bookingId);
      if (!b) return;
      setDayBookings(prev => prev.map(x => x.id === bookingId ? { ...x, start_time: newStart, end_time: newEnd } : x));
      await api.put(`/bookings/${bookingId}/update`, {
        booking_date: b.booking_date, start_time: newStart, end_time: newEnd,
        update_reason: 'Time updated via calendar drag'
      });
    } catch (err) {
      console.error('Failed to move booking:', err);
      fetchDayBookings(selectedStaff.id, toLocalDateStr(selectedDate));
    }
  };

  const handleBookingClick  = (booking) => setEditBooking(booking);
  const handleBookingSaved  = (updated)  => { setDayBookings(prev => prev.map(b => b.id === updated.id ? { ...b, ...updated } : b)); setEditBooking(null); };
  const handleBookingCancelled = (id)    => { setDayBookings(prev => prev.filter(b => b.id !== id)); setEditBooking(null); };

  const handleStaffSelect = (staff) => {
    setSelectedStaff(staff);
    setSelectedEventType(null);
    setSelectedSlot(null);
    setAvailableSlots([]);
    setSubmitResult(null);
    fetchEventTypes(staff.id);
    setView('meetingType');
  };

  const handleMeetingTypeSelect = (eventType) => {
    setSelectedEventType(eventType);
    setSelectedSlot(null);
    setSelectedDate(new Date());
    setSubmitResult(null);
    fetchAvailableSlots(selectedStaff.id, eventType.id);
    fetchDayBookings(selectedStaff.id, toLocalDateStr(new Date()));
    setView('calendar');
  };

  const handleDateSelect = (date) => {
    setSelectedDate(date);
    if (selectedStaff) fetchDayBookings(selectedStaff.id, toLocalDateStr(date));
  };

  const handleSlotSelect = (slot) => {
    setSelectedSlot(slot);
    setSubmitResult(null);
    // Auto-fill form if coming from Leads page
    if (leadDataRef.current) {
      const ld = leadDataRef.current;
      setFormData(prev => ({
        ...prev,
        customer_name: ld.customer_name || prev.customer_name,
        customer_phone: ld.phone_number || prev.customer_phone,
        customer_email: ld.email || prev.customer_email,
        meeting_reason: ld.needs_summary || ld.meeting_reason || prev.meeting_reason,
        language: ld.language || prev.language,
      }));
    }
    setView('form');
  };

  // Called by BookingPanel modal
  const handlePanelSubmit = async ({ slot, form, participant_ids = [] }) => {
    if (!form.customer_name.trim()) { setSubmitResult({ success: false, message: 'Please enter the Customer name' }); return; }
    if (!form.customer_phone.trim()) { setSubmitResult({ success: false, message: 'Please enter your phone number' }); return; }
    setSubmitting(true);
    setSubmitResult(null);
    try {
      const bookingData = {
        customer_name: form.customer_name.trim(),
        customer_phone: form.customer_phone.trim(),
        customer_email: form.customer_email.trim() || null,
        agent_id: selectedStaff.id,
        meeting_type: selectedEventType.id,
        meeting_reason: form.meeting_reason.trim() || null,
        booking_date: slot.date,
        start_time: slot.start_time,
        end_time: slot.end_time,
        connection_method: form.connection_method || null,
        language: form.language,
        participant_ids: participant_ids
      };
      const response = await api.post('/bookings/create', bookingData);
      const data = response.data !== undefined ? response.data : response;
      if (data && (data.success || data.id || data.data)) {
        setSubmitResult({ success: true, message: 'Booking confirmed!' });
        // Refresh day bookings to show the new one on the timeline
        fetchDayBookings(selectedStaff.id, slot.date);
      } else {
        setSubmitResult({ success: false, message: data?.message || 'Failed to create booking.' });
      }
    } catch (error) {
      setSubmitResult({ success: false, message: error.response?.data?.message || error.message || 'Failed to create booking' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };


// 👥 Participant functions
  const fetchAvailableParticipants = async () => {
    if (!selectedSlot || !selectedStaff) return;
    setLoadingParticipants(true);
    try {
      const response = await api.get('/bookings/available-participants', {
        params: {
          booking_date: selectedSlot.date,
          start_time: selectedSlot.start_time,
          end_time: selectedSlot.end_time,
          exclude_agent_id: selectedStaff.id
        }
      });
      const data = response.data;
      if (data && data.success) {
        setAvailableParticipants(data.data || []);
      }
    } catch (error) {
      console.error('Error fetching available participants:', error);
    }
    setLoadingParticipants(false);
  };

  const toggleParticipant = (agent) => {
    setSelectedParticipants(prev => {
      const exists = prev.find(p => p.id === agent.id);
      if (exists) return prev.filter(p => p.id !== agent.id);
      return [...prev, agent];
    });
  };

  const removeParticipant = (agentId) => {
    setSelectedParticipants(prev => prev.filter(p => p.id !== agentId));
  };
  
  const handleSubmitBooking = async (e) => {
    e.preventDefault();
    if (!formData.customer_name.trim()) {
      setSubmitResult({ success: false, message: 'Please enter the Customer name' });
      return;
    }
    if (!formData.customer_phone.trim()) {
      setSubmitResult({ success: false, message: 'Please enter your phone number' });
      return;
    }

    setSubmitting(true);
    setSubmitResult(null);

    try {
      const bookingData = {
        customer_name: formData.customer_name.trim(),
        customer_phone: formData.customer_phone.trim(),
        customer_email: formData.customer_email.trim() || null,
        agent_id: selectedStaff.id,
        meeting_type: selectedEventType.id,
        meeting_reason: formData.meeting_reason.trim() || null,
        booking_date: selectedSlot.date,
        start_time: selectedSlot.start_time,
        connection_method: formData.connection_method || null,
        end_time: selectedSlot.end_time,
        participant_ids: selectedParticipants.map(p => p.id)

      };

      const response = await api.post('/bookings/create', bookingData);
      const data = response.data !== undefined ? response.data : response;

      if (data && (data.success || data.id || data.data)) {
        setSubmitResult({
          success: true,
          message: 'Booking confirmed successfully!',
          booking: data.data || data
        });
        // Update lead status if booking was initiated from Leads page
        if (leadDataRef.current && leadDataRef.current.lead_id) {
          try {
            await api.patch(`/leads/${leadDataRef.current.lead_id}/status`, {
              status: 'demo_scheduled'
            });
            // Also update demo_date on the lead
            await api.put(`/leads/${leadDataRef.current.lead_id}`, {
              demo_date: selectedSlot.date + 'T' + selectedSlot.start_time,
              status: 'demo_scheduled'
            });
            console.log('✅ Lead updated to demo_scheduled');
          } catch (leadErr) {
            console.error('Failed to update lead status:', leadErr);
          }
          // Clean up
          localStorage.removeItem('booking_lead_data');
          leadDataRef.current = null;
        }
      } else {
        setSubmitResult({
          success: false,
          message: data?.message || 'Failed to create booking. Please try again.'
        });
      }
    } catch (error) {
      const errorMsg = error.response?.data?.message || error.message || 'Failed to create booking';
      setSubmitResult({ success: false, message: errorMsg });
    } finally {
      setSubmitting(false);
    }
  };

  const handleNewBooking = () => {
    setView('staff');
    setSelectedStaff(null);
    setSelectedEventType(null);
    setSelectedSlot(null);
    setAvailableSlots([]);
    setSubmitResult(null);
    setFormData({ customer_name: '', customer_phone: '', customer_email: '', language: 'ar', meeting_reason: '',
    connection_method: '' });
    setSelectedParticipants([]);
    setAvailableParticipants([]);
    setShowParticipantSelector(false);
    // Clean up lead data
    localStorage.removeItem('booking_lead_data');
    leadDataRef.current = null;
  };

  // ── Calendar helpers ──

  const getCalendarDays = () => {
    const year = selectedDate.getFullYear();
    const month = selectedDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const days = [];
    const firstDayOfWeek = firstDay.getDay();
    for (let i = firstDayOfWeek - 1; i >= 0; i--) {
      days.push({ date: new Date(year, month, -i), isCurrentMonth: false });
    }
    for (let i = 1; i <= lastDay.getDate(); i++) {
      days.push({ date: new Date(year, month, i), isCurrentMonth: true });
    }
    return days;
  };

  // ✅ Use LOCAL date string for comparison (fixes UTC timezone shift bug)
  const dateHasSlots = (date) => {
    const dateStr = toLocalDateStr(date);
    return availableSlots.some(s => s.date === dateStr && s.available !== false);
  };

const getSlotsForDate = (date) => {
    const dateStr = toLocalDateStr(date);
    const now = new Date();
    const isToday = dateStr === toLocalDateStr(now);
    const currentHour = now.getHours();
    const currentMin = now.getMinutes();

    return availableSlots.filter(s => {
      if (s.date !== dateStr || s.available === false) return false;
      // If today, hide past slots
      if (isToday && s.start_time) {
        const [h, m] = s.start_time.split(':').map(Number);
        if (h < currentHour || (h === currentHour && m <= currentMin)) return false;
      }
      return true;
    });
  };

  return (
    <Layout>
      <div className="booking-calendar-container">
        {/* Header */}
        <div className="page-header mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="page-title">📅 Booking Calendar</h1>
              <p className="page-subtitle">Schedule appointments with our team</p>
            </div>
            <button className="btn btn-outline" onClick={handleNewBooking}>
              <span className="nav-icon">🔄</span> Start Over
            </button>
          </div>
        </div>

        {/* Breadcrumb */}
        <div className="card mb-4 p-4">
          <div className="flex items-center gap-2">
            <button className={`btn ${view === 'staff' ? 'btn-primary' : 'btn-ghost'}`}
              onClick={() => setView('staff')}>
              1. Choose Staff
            </button>
            <span className="text-secondary">→</span>
            <button className={`btn ${view === 'meetingType' ? 'btn-primary' : 'btn-ghost'}`}
              disabled={!selectedStaff}
              onClick={() => selectedStaff && setView('meetingType')}>
              2. Meeting Type
            </button>
            <span className="text-secondary">→</span>
            <button className={`btn ${view === 'calendar' ? 'btn-primary' : 'btn-ghost'}`}
              disabled={!selectedEventType}
              onClick={() => selectedEventType && setView('calendar')}>
              3. Pick Date & Time
            </button>
            <span className="text-secondary">→</span>
            <button className={`btn ${view === 'form' ? 'btn-primary' : 'btn-ghost'}`}
              disabled={!selectedSlot}>
              4. Book Appointment
            </button>
          </div>
        </div>

        {/* ══════════════════════════════════════════ */}
        {/* STEP 1: Choose Staff                       */}
        {/* ══════════════════════════════════════════ */}
        {view === 'staff' && (
          <div className="card p-6">
            <h3 className="text-primary mb-4">Choose who you'd like to meet with</h3>

            {staffLoading ? (
              <div className="loading-spinner">Loading available staff...</div>
            ) : staffList.length === 0 ? (
              <div className="empty-state text-center p-8">
                <span className="nav-icon" style={{fontSize: '48px'}}>👥</span>
                <p className="text-secondary mt-4">No staff available for booking</p>
              </div>
            ) : (
              <div className="grid grid-cols-3 gap-4">
                {staffList.map((staff) => (
                  <div key={staff.id} className="card p-6 glow-on-hover">
                    <div className="flex items-center mb-4">
                      <div className="user-avatar">
                        {staff.full_name?.charAt(0) || '?'}
                      </div>
                      <div className="ml-4">
                        <h3 className="text-primary">{staff.full_name}</h3>
                        <p className="text-secondary">{staff.email}</p>
                      </div>
                    </div>
                    {staff.timezone && (
                      <div className="text-tertiary mb-4">
                        <span className="nav-icon">🌍</span> {staff.timezone}
                      </div>
                    )}
                    <button className="btn btn-primary w-full"
                      onClick={() => handleStaffSelect(staff)}>
                      Select {staff.full_name?.split(' ')[0]}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ══════════════════════════════════════════ */}
        {/* STEP 2: Select Meeting Type                */}
        {/* ══════════════════════════════════════════ */}
        {view === 'meetingType' && (
          <div>
            <div className="card mb-4 p-4">
              <div className="flex items-center justify-between">
                <div>
                  <span className="text-secondary">With: </span>
                  <span className="text-primary font-bold">{selectedStaff?.full_name}</span>
                </div>
                <button className="btn btn-ghost btn-sm" onClick={() => setView('staff')}>
                  Change
                </button>
              </div>
            </div>

            <div className="card p-6">
              <h3 className="text-primary mb-4">What type of meeting would you like?</h3>

              {eventTypesLoading ? (
                <div className="loading-spinner">Loading meeting types...</div>
              ) : eventTypes.length === 0 ? (
                <div className="empty-state text-center p-8">
                  <span className="nav-icon" style={{fontSize: '48px'}}>📋</span>
                  <p className="text-secondary mt-4">No meeting types available for this staff</p>
                </div>
              ) : (
                <div className="grid grid-cols-3 gap-4">
                  {eventTypes.map((et) => (
                    <div key={et.id} className="card p-6 glow-on-hover">
                      <div className="mb-4">
                        <span style={{fontSize: '24px'}}>{MEETING_ICONS[et.id] || MEETING_ICONS[et.slug] || '📅'}</span>
                        {et.color && (
                          <span style={{
                            width: '12px', height: '12px', borderRadius: '50%',
                            backgroundColor: et.color, display: 'inline-block', marginLeft: '8px'
                          }} />
                        )}
                        <h4 className="text-primary text-lg mt-2">{et.title}</h4>
                        {et.description && <p className="text-secondary mt-2">{et.description}</p>}
                      </div>
                      <div className="flex items-center justify-between mb-4">
                        <span className="text-tertiary">
                          <span className="nav-icon">⏱️</span> {et.duration} minutes
                        </span>
                      </div>
                      {et.connection_methods && et.connection_methods.length > 0 && (
                        <div className="mb-3">
                          {et.connection_methods.map((m, i) => (
                            <span key={i} className="badge badge-ghost mr-1" style={{fontSize: '11px'}}>
                              {m === 'google_meet' ? '📹 Meet' : m === 'zoom' ? '💻 Zoom' :
                               m === 'phone' ? '📞 Phone' : m === 'in_person' ? '🏢 In Person' : m}
                            </span>
                          ))}
                        </div>
                      )}
                      <button className="btn btn-primary w-full"
                        onClick={() => handleMeetingTypeSelect(et)}>
                        Select This
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ══════════════════════════════════════════ */}
        {/* STEP 3: Pick Date & Time                   */}
        {/* ══════════════════════════════════════════ */}
        {view === 'calendar' && selectedStaff && selectedEventType && (
          <>
          <div>
            <div className="card mb-4 p-4">
              <div className="flex items-center gap-6">
                <div>
                  <span className="text-secondary">With: </span>
                  <span className="text-primary font-bold">{selectedStaff.full_name}</span>
                </div>
                <div>
                  <span className="text-secondary">Meeting: </span>
                  <span className="text-primary font-bold">{selectedEventType.title}</span>
                  <span className="text-tertiary ml-2">({selectedEventType.duration} min)</span>
                </div>
                <button className="btn btn-ghost btn-sm ml-auto"
                  onClick={() => setView('meetingType')}>
                  Change Type
                </button>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-6">
              <div className="card p-6">
                <h3 className="text-primary mb-4">Select Date</h3>
                <div className="calendar-grid">
                  <div className="calendar-header mb-2">
                    <button className="btn btn-ghost" onClick={() => {
                      const d = new Date(selectedDate);
                      d.setMonth(d.getMonth() - 1);
                      setSelectedDate(d);
                    }}>←</button>
                    <span className="text-primary">
                      {selectedDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                    </span>
                    <button className="btn btn-ghost" onClick={() => {
                      const d = new Date(selectedDate);
                      d.setMonth(d.getMonth() + 1);
                      setSelectedDate(d);
                    }}>→</button>
                  </div>

                  <div className="calendar-days-grid" style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(7, 1fr)',
                    gap: '2px'
                  }}>
                    {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
                      <div key={day} className="calendar-day-header text-secondary"
                        style={{ textAlign: 'center', padding: '4px', fontSize: '12px' }}>
                        {day}
                      </div>
                    ))}
                    {getCalendarDays().map((day, index) => {
                      const todayStr = toLocalDateStr(new Date());
                      const dayStr = toLocalDateStr(day.date);
                      const isToday = dayStr === todayStr;
                      const isSelected = dayStr === toLocalDateStr(selectedDate);
                      const isPast = dayStr < todayStr;
                      const hasSlots = dateHasSlots(day.date);
                      return (
                        <button key={index}
                          className={`calendar-day-cell ${
                            !day.isCurrentMonth ? 'calendar-day-other' : ''
                          } ${isToday ? 'calendar-day-today' : ''} ${
                            isSelected ? 'calendar-day-selected' : ''
                          } ${hasSlots && day.isCurrentMonth && !isPast ? 'calendar-day-has-slots' : ''}`}
                          style={{
                            textAlign: 'center',
                            padding: '8px 4px',
                            minWidth: '0',
                            position: 'relative'
                          }}
                          onClick={() => day.isCurrentMonth && !isPast && handleDateSelect(day.date)}
                          disabled={!day.isCurrentMonth || isPast}
                        >
                          {day.date.getDate()}
                          {hasSlots && day.isCurrentMonth && !isPast && (
                            <span style={{
                              position: 'absolute', bottom: '1px', left: '50%',
                              transform: 'translateX(-50%)', color: '#10b981',
                              fontSize: '16px', lineHeight: '1'
                            }}>•</span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>

              <div className="card p-4 col-span-2">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                  <h3 className="text-primary">
                    {selectedDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
                  </h3>
                  <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>
                    💡 Click or drag to select a time
                  </span>
                </div>

                {slotsLoading || dayBookingsLoading ? (
                  <div className="loading-spinner">Loading schedule...</div>
                ) : (() => {
                  const daySlots = getSlotsForDate(selectedDate);
                  if (daySlots.length === 0) {
                    return (
                      <div className="empty-state text-center p-8">
                        <span className="nav-icon" style={{fontSize: '48px'}}>📆</span>
                        <p className="text-secondary mt-4">No available slots for this date</p>
                        <p className="text-tertiary mt-2">
                          {toLocalDateStr(selectedDate) === toLocalDateStr(new Date())
                            ? 'No more slots today. Try a future date.'
                            : 'This day may be outside working hours.'}
                        </p>
                      </div>
                    );
                  }
                  // Compute working hours bounds from available slots
                  const allStarts = daySlots.map(s => tMins(s.start_time));
                  const allEnds   = daySlots.map(s => tMins(s.end_time));
                  const dayStart  = Math.min(...allStarts);
                  const dayEnd    = Math.max(...allEnds);

                  return (
                    <BookingTimeline
                      dayStart={dayStart}
                      dayEnd={dayEnd}
                      dayBookings={dayBookings}
                      availableSlots={daySlots}
                      defaultDuration={selectedEventType.duration}
                      onTimeSelect={(fromMins, toMins) => {
                        setSubmitResult(null);
                        setBookingPanel({ fromMins, toMins });
                      }}
                      onBookingMove={handleBookingMove}
                      onBookingClick={handleBookingClick}
                    />
                  );
                })()}
              </div>
            </div>
          </div>

          {/* BookingPanel modal */}
          {bookingPanel && (
            <BookingPanel
              initialFrom={bookingPanel.fromMins}
              initialTo={bookingPanel.toMins}
              date={toLocalDateStr(selectedDate)}
              staff={selectedStaff}
              eventType={selectedEventType}
              dayBookings={dayBookings}
              dayEnd={(() => {
                const daySlots = getSlotsForDate(selectedDate);
                return daySlots.length ? Math.max(...daySlots.map(s => tMins(s.end_time))) : 19 * 60;
              })()}
              onSubmit={handlePanelSubmit}
              onClose={() => { setBookingPanel(null); setSubmitResult(null); }}
              submitting={submitting}
              submitResult={submitResult}
            />
          )}

          {/* EditBookingPanel modal */}
          {editBooking && (
            <EditBookingPanel
              booking={editBooking}
              onClose={() => setEditBooking(null)}
              onSaved={handleBookingSaved}
              onCancelled={handleBookingCancelled}
            />
          )}
          </>
        )}

        {/* ══════════════════════════════════════════ */}
        {/* STEP 4: Booking Form                       */}
        {/* ══════════════════════════════════════════ */}
        {view === 'form' && selectedSlot && (
          <div className="card p-6">

            {submitResult && submitResult.success ? (
              <div className="text-center p-8">
                <span style={{fontSize: '64px'}}>✅</span>
                <h3 className="text-primary mt-4 mb-2">Booking Confirmed!</h3>
                <p className="text-secondary mb-6">{submitResult.message}</p>

                <div className="card p-4 mb-6" style={{background: 'var(--bg-tertiary)', maxWidth: '400px', margin: '0 auto'}}>
                  <div className="grid grid-cols-2 gap-2 text-tertiary" style={{textAlign: 'left'}}>
                    <div><strong>With:</strong></div>
                    <div>{selectedStaff?.full_name}</div>
                    <div><strong>Type:</strong></div>
                    <div>{selectedEventType?.title}</div>
                    <div><strong>Date:</strong></div>
                    <div>{selectedSlot?.date}</div>
                    <div><strong>Time:</strong></div>
                    <div>{selectedSlot?.start_time} - {selectedSlot?.end_time}</div>
                  </div>
                </div>

                <button className="btn btn-primary" onClick={handleNewBooking}>
                  📅 Book Another Appointment
                </button>
                {leadDataRef.current && (
                  <button className="btn btn-outline ml-3" onClick={() => navigate('/leads')} style={{marginTop: '8px'}}>
                    ← Back to Leads
                  </button>
                )}
              </div>
            ) : (
              <>
                <h3 className="text-primary mb-6">Complete Your Booking</h3>

                {leadDataRef.current && (
                  <div className="card p-4 mb-4" style={{background: '#6366f122', border: '1px solid #6366f1'}}>
                    <p style={{color: '#818cf8', margin: 0}}>
                      📋 Pre-filled from Lead: <strong>{leadDataRef.current.customer_name}</strong>
                      {leadDataRef.current.company_name ? ` - ${leadDataRef.current.company_name}` : ''}
                    </p>
                  </div>
                )}

                {submitResult && !submitResult.success && (
                  <div className="card p-4 mb-4" style={{background: '#ff4d4f22', border: '1px solid #ff4d4f'}}>
                    <p style={{color: '#ff4d4f'}}>❌ {submitResult.message}</p>
                  </div>
                )}

                <form className="booking-form" onSubmit={handleSubmitBooking}>
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <div className="form-group">
                      <label className="form-label">Customer Name *</label>
                      <input type="text" className="form-input" placeholder="Enter Customer name"
                        name="customer_name" value={formData.customer_name}
                        onChange={handleFormChange} required />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Phone Number (WhatsApp) *</label>
                      <input type="tel" className="form-input" placeholder="+20 123 456 7890"
                        name="customer_phone" value={formData.customer_phone}
                        onChange={handleFormChange} required />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Email (Optional)</label>
                      <input type="email" className="form-input" placeholder="your@email.com"
                        name="customer_email" value={formData.customer_email}
                        onChange={handleFormChange} />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Preferred Language</label>
                      <select className="form-select" name="language"
                        value={formData.language} onChange={handleFormChange}>
                        <option value="ar">العربية (Arabic)</option>
                        <option value="en">English</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Meeting Method *</label>
                      <select className="form-select" name="connection_method"
                        value={formData.connection_method} onChange={handleFormChange} required>
                        <option value="">-- Select Method --</option>
                        {(() => {
                          try {
                            const methods = typeof selectedEventType?.connection_methods === 'string'
                              ? JSON.parse(selectedEventType.connection_methods)
                              : (selectedEventType?.connection_methods || []);
                            return methods.map((m, i) => (
                              <option key={i} value={m}>
                                {m === 'google_meet' ? '📹 Google Meet' : m === 'zoom' ? '💻 Zoom' :
                                 m === 'phone' ? '📞 Phone Call' : m === 'in_person' ? '🏢 In Person' : m}
                              </option>
                            ));
                          } catch(e) { return null; }
                        })()}
                      </select>
                    </div>
                  </div>

                  <div className="form-group mb-6">
                    <label className="form-label">What would you like to discuss?</label>
                    <textarea className="form-textarea"
                      placeholder="Briefly describe the purpose of your meeting..."
                      rows="3" name="meeting_reason" value={formData.meeting_reason}
                      onChange={handleFormChange}></textarea>
                  </div>

                  <div className="card p-4 mb-6" style={{background: 'var(--bg-tertiary)'}}>
                    <h4 className="text-secondary mb-3">📋 Booking Summary</h4>
                    <div className="grid grid-cols-2 gap-2 text-tertiary">
                      <div><strong>With:</strong></div>
                      <div>{selectedStaff?.full_name}</div>
                      <div><strong>Meeting Type:</strong></div>
                      <div>{selectedEventType?.title}</div>
                      <div><strong>Duration:</strong></div>
                      <div>{selectedEventType?.duration} minutes</div>
                      <div><strong>Date:</strong></div>
                      <div>{selectedSlot?.date ? new Date(selectedSlot.date + 'T12:00:00').toLocaleDateString('en-US', {
                        weekday: 'long', month: 'long', day: 'numeric', year: 'numeric'
                      }) : ''}</div>
                      <div><strong>Time:</strong></div>
                      <div>{selectedSlot?.start_time} - {selectedSlot?.end_time}</div>
                      {selectedParticipants.length > 0 && (
                        <>
                          <div><strong>Participants:</strong></div>
                          <div>{selectedStaff?.full_name} + {selectedParticipants.map(p => p.full_name).join(', ')}</div>
                        </>
                      )}
                    </div>
                  </div>

{/* 👥 Add Participants Section */}
                  <div style={{
                    marginBottom: '24px', padding: '20px',
                    background: 'rgba(139, 92, 246, 0.08)',
                    borderRadius: '12px',
                    border: '1px solid rgba(139, 92, 246, 0.25)'
                  }}>
                    <div style={{
                      display: 'flex', justifyContent: 'space-between',
                      alignItems: 'center', marginBottom: selectedParticipants.length > 0 || showParticipantSelector ? '16px' : '0'
                    }}>
                      <h4 style={{ margin: 0, color: '#a78bfa', fontSize: '15px' }}>
                        👥 Additional Participants (Optional)
                      </h4>
                      <button type="button"
                        onClick={() => {
                          setShowParticipantSelector(!showParticipantSelector);
                          if (!showParticipantSelector) fetchAvailableParticipants();
                        }}
                        className="btn btn-outline btn-sm"
                        style={{
                          color: showParticipantSelector ? '#f87171' : '#a78bfa',
                          borderColor: showParticipantSelector ? '#f87171' : '#a78bfa',
                          fontSize: '13px'
                        }}>
                        {showParticipantSelector ? '✕ Close' : '+ Add Team Member'}
                      </button>
                    </div>

                    {/* Selected Participants Tags */}
                    {selectedParticipants.length > 0 && (
                      <div style={{
                        display: 'flex', flexWrap: 'wrap', gap: '8px',
                        marginBottom: showParticipantSelector ? '16px' : '0'
                      }}>
                        {selectedParticipants.map(p => (
                          <span key={p.id} style={{
                            display: 'inline-flex', alignItems: 'center', gap: '6px',
                            padding: '5px 12px',
                            background: 'rgba(34, 197, 94, 0.15)',
                            border: '1px solid rgba(34, 197, 94, 0.4)',
                            borderRadius: '20px', fontSize: '13px', color: '#4ade80'
                          }}>
                            <span style={{
                              width: '22px', height: '22px', borderRadius: '50%',
                              background: '#8b5cf6', display: 'inline-flex',
                              alignItems: 'center', justifyContent: 'center',
                              fontSize: '11px', color: 'white'
                            }}>
                              {p.full_name?.charAt(0)?.toUpperCase()}
                            </span>
                            {p.full_name}
                            <button type="button" onClick={() => removeParticipant(p.id)}
                              style={{
                                background: 'none', border: 'none', color: '#f87171',
                                cursor: 'pointer', fontSize: '16px', padding: '0 2px', lineHeight: '1'
                              }}>×</button>
                          </span>
                        ))}
                      </div>
                    )}

                    {/* Participant Selector Grid */}
                    {showParticipantSelector && (
                      <div style={{
maxHeight: 'none', overflowY: 'visible',
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                        gap: '8px'
                      }}>
                        {loadingParticipants ? (
                          <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '20px', color: '#9ca3af' }}>
                            Loading team members...
                          </div>
                        ) : availableParticipants.length === 0 ? (
                          <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '20px', color: '#9ca3af' }}>
                            No other team members found
                          </div>
                        ) : (
                          availableParticipants.map(agent => {
                            const isSelected = selectedParticipants.find(p => p.id === agent.id);
                            const isBusy = !agent.is_available;
                            return (
                              <div key={agent.id}
                                onClick={() => !isBusy && toggleParticipant(agent)}
                                style={{
                                  display: 'flex', alignItems: 'center', gap: '10px',
                                  padding: '10px 12px', borderRadius: '10px',
                                  cursor: isBusy ? 'not-allowed' : 'pointer',
                                  opacity: isBusy ? 0.4 : 1,
                                  background: isSelected ? 'rgba(139, 92, 246, 0.25)' : 'rgba(255,255,255,0.04)',
                                  border: `2px solid ${isSelected ? '#8b5cf6' : isBusy ? '#ef4444' : 'rgba(255,255,255,0.08)'}`,
                                  transition: 'all 0.2s'
                                }}>
                                <div style={{
                                  width: '34px', height: '34px', borderRadius: '50%',
                                  background: isSelected ? '#8b5cf6' : '#374151',
                                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                                  fontSize: '13px', color: 'white', flexShrink: 0
                                }}>
                                  {isSelected ? '✓' : agent.full_name?.charAt(0)?.toUpperCase()}
                                </div>
                                <div style={{ overflow: 'hidden' }}>
                                  <div style={{
                                    fontSize: '13px', fontWeight: '600',
                                    color: isBusy ? '#9ca3af' : 'white',
                                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'
                                  }}>{agent.full_name}</div>
                                  {isBusy && (
                                    <div style={{ fontSize: '11px', color: '#ef4444' }}>⚠ Busy</div>
                                  )}
                                </div>
                              </div>
                            );
                          })
                        )}
                      </div>
                    )}
                  </div>


                  <div className="flex gap-3">
                    <button type="button" className="btn btn-outline" onClick={() => setView('calendar')}>
                      ← Back
                    </button>
                    <button type="submit" className="btn btn-primary flex-1" disabled={submitting}>
                      {submitting ? '⏳ Booking...' : '✅ Confirm Booking'}
                    </button>
                  </div>
                </form>
              </>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default BookingCalendar;